import type { EmployeeData } from '../../../../../../app/features/employee/emp-dashboard/emp-dashboard.type';
import ModuleGuard from '../../../../../../components/Guards/ModuleGuard';
import MODULES from '../../../../../../config/modules.config';
import { snakeToCamel } from '../../../../../../utils/string.utils';
import { formatDate } from '@/utils';

interface JobInfoProps {
  employee?: EmployeeData & {
    is_geo_enable?: boolean;
    is_overtime_enable?: boolean;
    is_face_enable?: boolean;
    fence_attendance_permission?: string;
    office?: any;
    radius?: number;
    latitude?: number;
    longitude?: number;
  };
  loading?: boolean;
}

const JobInfo = ({ employee, loading }: JobInfoProps) => {
  if (loading) {
    return (
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        <div className="space-y-6">
          <div className="bg-theme-slate-800 rounded-xl p-6 h-[400px] animate-pulse" />
          <div className="bg-theme-slate-800 rounded-xl p-6 h-[300px] animate-pulse" />
        </div>
        <div className="space-y-6">
          <div className="bg-theme-slate-800 rounded-xl p-6 h-[150px] animate-pulse" />
          <div className="bg-theme-slate-800 rounded-xl p-6 h-[100px] animate-pulse" />
          <div className="bg-theme-slate-800 rounded-xl p-6 h-[100px] animate-pulse" />
        </div>
      </div>
    );
  }

  const formatPermission = (permission?: string) => {
    switch (permission) {
      case 'BOTH':
        return 'Check - In & Out';
      case 'CHECK_IN_ONLY':
        return 'Check - In';
      case 'CHECK_OUT_ONLY':
        return 'Check - Out';
      default:
        return permission || '--';
    }
  };

  return (
    <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
      <div className="space-y-6">
        {/* Employee Info */}
        <div className="bg-theme-slate-800 rounded-xl p-6 space-y-10">
          <h6 className="text-2xl text-white font-black">Employee Info</h6>
          <div className="space-y-10">
            <InfoItem
              label="Join Date"
              value={
                employee?.joining_date
                  ? formatDate(employee.joining_date, 'YYYY/MM/DD')
                  : '--'
              }
            />
            <InfoItem
              label="Employee Type"
              value={
                employee?.employee_type
                  ? snakeToCamel(employee.employee_type)
                  : '--'
              }
            />
            <InfoItem
              label="Employee ID"
              value={
                employee?.employee_id ? `ID ${employee.employee_id}` : '--'
              }
            />
            <InfoItem
              label="Department"
              value={employee?.department?.name || '--'}
            />
            <InfoItem
              label="Designation"
              value={employee?.designation?.name || '--'}
            />
          </div>
        </div>

        {/* Geo Fencing */}
        <div className="bg-theme-slate-800 rounded-xl p-6 space-y-10">
          <h6 className="text-2xl text-white font-black">Geo Fencing</h6>
          <div className="space-y-10">
            <InfoItem
              label="Status"
              value={employee?.is_geo_enable ? 'Active' : 'Inactive'}
            />
            <InfoItem
              label="Fence Attendance Permission"
              value={formatPermission(employee?.fence_attendance_permission)}
            />
            <InfoItem
              label="Latitude"
              value={employee?.latitude ? `${employee.latitude}` : '--'}
            />
            <InfoItem
              label="Longitude"
              value={employee?.longitude ? `${employee.longitude}` : '--'}
            />
            <InfoItem
              label="Radious"
              value={employee?.radius ? `${employee.radius} Meter` : '--'}
            />
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Office Info */}
        <div className="bg-theme-slate-800 rounded-xl p-6 space-y-8">
          <h6 className="text-2xl text-white font-black">Office Info</h6>
          <div className="space-y-6">
            <InfoItem
              label="Office Name"
              value={employee?.office?.name || '--'}
            />
            <InfoItem
              label="Location"
              value={employee?.office?.location || '--'}
            />
          </div>
        </div>

        {/* Over Time */}
        <ModuleGuard moduleId={MODULES.EMPLOYEE_POLICIES} fallback={<div />}>
          <div className="bg-theme-slate-800 rounded-xl p-6 space-y-8">
            <h6 className="text-2xl text-white font-black">Over Time</h6>
            <div className="space-y-6">
              <InfoItem
                label="Status"
                value={employee?.is_overtime_enable ? 'Active' : 'Inactive'}
              />
            </div>
          </div>
        </ModuleGuard>

        {/* Facial Recognition */}
        <div className="bg-theme-slate-800 rounded-xl p-6 space-y-8">
          <h6 className="text-2xl text-white font-black">Facial Recognition</h6>
          <div className="space-y-6">
            <InfoItem
              label="Status"
              value={
                employee?.is_face_recognition_enable ? 'Active' : 'Inactive'
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="space-y-1">
      <h6 className="text-base text-gray-400 font-normal">{label}</h6>
      <h6 className="text-xl text-white font-medium">{value}</h6>
    </div>
  );
};

export default JobInfo;
